import React, { useEffect, useState } from "react";
import Slider from "@mui/material/Slider";
import "./InventoryPriceFilters.scss";

export const PRICE_PRESETS: Record<string, Record<string, any>> = {
  ALL: {}, UNDER_20000: { maxSellPrice: "20000", maxSellPriceInclusive: false },
  UNDER_25000: { maxSellPrice: "25000", maxSellPriceInclusive: false },
  UNDER_50000: { maxSellPrice: "50000", maxSellPriceInclusive: false },
  UNDER_100000: { maxSellPrice: "100000", maxSellPriceInclusive: false },
  FROM_20000: { minSellPrice: "20000", maxSellPrice: "30000" },
  FROM_30000: { minSellPrice: "30000", minSellPriceInclusive: false, maxSellPrice: "50000" },
  OVER_50000: { minSellPrice: "50000", minSellPriceInclusive: false },
  PRICED: { priceStatus: "PRICED" }, UNPRICED: { priceStatus: "UNPRICED" },
};
/** Slider range; the top stop means "no upper limit". */
export const SLIDER_MAX = 200000;
const SLIDER_STEP = 1000;
const QUICK_PICKS: Array<[string, string]> = [["UNDER_25000", "Under ₹25k"], ["UNDER_50000", "Under ₹50k"], ["UNDER_100000", "Under ₹1 lakh"]];
export const formatRupees = (amount: number) => `₹${amount.toLocaleString("en-IN")}`;
/** Slider handles for the current filter: unset bounds sit at the ends. */
export function sliderRange(value: Record<string, any>): [number, number] {
  const clamp = (n: number) => Math.min(SLIDER_MAX, Math.max(0, Math.round(n)));
  const min = Number(value.minSellPrice), max = Number(value.maxSellPrice);
  return [value.minSellPrice && Number.isFinite(min) ? clamp(min) : 0, value.maxSellPrice && Number.isFinite(max) ? clamp(max) : SLIDER_MAX];
}
/** Filter for a slider position: ends mean "no limit"; both bounds inclusive. */
export function rangeFromSlider([lo, hi]: [number, number], includeUnpriced: boolean): Record<string, any> {
  return { preset: "CUSTOM", minSellPrice: lo > 0 ? String(lo) : "", maxSellPrice: hi < SLIDER_MAX ? String(hi) : "", includeUnpriced };
}
// Exact decimal comparison avoids rounding very close boundaries in browser validation.
export function compareAmounts(a: string, b: string): number {
  const [ai, af = ""] = a.replace(/^0+(?=\d)/, "").split(".");
  const [bi, bf = ""] = b.replace(/^0+(?=\d)/, "").split(".");
  if (ai.length !== bi.length) return ai.length - bi.length;
  const size = Math.max(af.length, bf.length);
  const left = `${ai}.${af.padEnd(size, "0")}`, right = `${bi}.${bf.padEnd(size, "0")}`;
  return left === right ? 0 : left > right ? 1 : -1;
}
export function customPriceError(min: string, max: string): string {
  if ([min, max].some((value) => value !== "" && !/^\d+(\.\d+)?$/.test(value))) return "Enter a valid non-negative price.";
  return min && max && compareAmounts(min, max) > 0 ? "Minimum price must not exceed maximum price." : "";
}
export default function InventoryPriceFilters({ value = {}, onChange }: { value?: Record<string, any>; onChange: (value: Record<string, any>) => void }) {
  const [min, setMin] = useState(value.minSellPrice || "");
  const [max, setMax] = useState(value.maxSellPrice || "");
  const [error, setError] = useState("");
  useEffect(() => { setMin(value.minSellPrice || ""); setMax(value.maxSellPrice || ""); setError(""); }, [value.minSellPrice, value.maxSellPrice, value.preset]);
  const preset = value.preset || "ALL";
  const range = preset === "CUSTOM" || Boolean(value.minSellPrice || value.maxSellPrice);
  const statusOnly = preset === "PRICED" || preset === "UNPRICED";
  const [slide, setSlide] = useState<[number, number]>(sliderRange(value));
  useEffect(() => { setSlide(sliderRange(value)); }, [value.minSellPrice, value.maxSellPrice, value.preset]);
  const label = (n: number, top: boolean) => (top && n >= SLIDER_MAX ? `${formatRupees(SLIDER_MAX)}+` : formatRupees(n));
  return <section className="qf-more-section hkp-price-filters" aria-label="Selling price filters">
    <div className="hkp-price-filters__quick" role="group" aria-label="Quick price ranges">
      {QUICK_PICKS.map(([key, text]) => <button key={key} type="button" aria-pressed={preset === key}
        className={`hkp-price-chip${preset === key ? " is-active" : ""}`}
        onClick={() => { setError(""); onChange(preset === key ? { preset: "ALL" } : { ...PRICE_PRESETS[key], preset: key }); }}>{text}</button>)}
    </div>
    {!statusOnly && <div className="hkp-price-filters__slider">
      <p className="hkp-price-filters__range" aria-live="polite">{slide[0] === 0 && slide[1] >= SLIDER_MAX ? "Any price"
        : `${label(slide[0], false)} – ${label(slide[1], true)}`}</p>
      <Slider value={slide} min={0} max={SLIDER_MAX} step={SLIDER_STEP} disableSwap
        getAriaLabel={(i) => (i === 0 ? "Lowest price" : "Highest price")}
        getAriaValueText={(n, i) => label(n, i === 1)}
        onChange={(_, next) => setSlide(next as [number, number])}
        onChangeCommitted={(_, next) => { setError(""); onChange(rangeFromSlider(next as [number, number], value.includeUnpriced === true)); }} />
    </div>}
    <label>Selling Price <select aria-label="Selling price range" value={preset} onChange={(e) => {
      setError(""); onChange({ ...(PRICE_PRESETS[e.target.value] || {}), preset: e.target.value });
    }}>
      <option value="ALL">Any price</option><option value="UNDER_20000">Under ₹20,000</option><option value="UNDER_25000">Under ₹25,000</option><option value="UNDER_50000">Under ₹50,000</option><option value="UNDER_100000">Under ₹1,00,000</option>
      <option value="FROM_20000">₹20,000 – ₹30,000 (inclusive)</option><option value="FROM_30000">Above ₹30,000 – ₹50,000</option>
      <option value="OVER_50000">Above ₹50,000</option><option value="CUSTOM">Custom range</option>
      <option value="PRICED">Priced only</option><option value="UNPRICED">Unpriced only</option>
    </select></label>
    {preset === "CUSTOM" && <div className="hkp-price-filters__custom">
      <label>Minimum price<input aria-label="Minimum price" inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} /></label>
      <label>Maximum price<input aria-label="Maximum price" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} /></label>
      <button type="button" className="btn btn-hastkala-outline" onClick={() => {
        const problem = customPriceError(min.trim(), max.trim()); setError(problem);
        if (!problem) onChange({ preset: "CUSTOM", minSellPrice: min.trim(), maxSellPrice: max.trim(), includeUnpriced: value.includeUnpriced === true });
      }}>Apply price range</button>
    </div>}
    {range && <label><input type="checkbox" checked={value.includeUnpriced === true} onChange={(e) => onChange({ ...value, includeUnpriced: e.target.checked })} />Include unpriced products</label>}
    {error && <p role="alert">{error}</p>}
  </section>;
}
